<template>
  <b-modal
    ref="modal"
    :visible="show"
    size="lg"
    @hidden="$emit('update:show', false)"
    hide-header
    hide-footer
    hide-header-close
    scrollable
  >
    <div class="kr-google-connect paragraph-14">
      <h2 class="text-large bold header">
        Auto update KR via Google sheets
      </h2>
      <div class="px-3">
        <div class="pb-2 aligner d-flex align-items-center py-2">
          <i class="google-sheet-icon"></i>
          <span class="pl-2">Enter link to your Google Sheets</span>
        </div>
        <text-area
          :value="formData.spreadsheetUrl"
          @input="onURLInput"
          :rows="2"
          placeholder="Enter link to your Google Sheets"
          :errored="!!formError.spreadsheetUrl"
          :error-message="
            formError.permission ? '' : formError.spreadsheetUrl && formError.spreadsheetUrl[0]
          "
          :autoFocus="true"
        >
          <template #error v-if="formError.permission">
            <div class="py-2">
              Cannot access spreadsheet, please authorize access with your Google account.
            </div>
            <div>
              <b-button variant="outline-dark" class="rounded-pill px-5" @click="authorizeGoogle">
                <img src="~@/assets/google.svg" /> Continue with Google
              </b-button>
            </div>
          </template>
        </text-area>
      </div>
      <div class="aligner skeleton mx-3" v-if="fetchingSheetInfo">
        <avatar-title class="mb-3" />
        <avatar-title class="mb-3" />
        <avatar-title />
      </div>
      <div class="aligner mx-3" v-else-if="sheetInfo">
        <div class="mb-4">
          <div class="d-flex align-items-center">
            <i class="google-sheet-icon"></i>
            <h3 class="text-big bold m-0 pl-2">{{ spreadsheetName }}</h3>
          </div>
        </div>
        <form @submit.prevent="connect" class="mb-4">
          <div class="d-flex mb-5">
            <div class="choose-sheet-field">
              <label>Choose sheet</label>
              <select
                @change="onSheetChange"
                class="custom-select grey-bg thin"
                :class="{ 'errored-field': formError.sheetTitle }"
                v-model="formData.sheetTitle"
              >
                <option selected disabled value="">Select sheet</option>
                <option :value="sheet" v-for="sheet in sheets" :key="sheet">{{ sheet }}</option>
              </select>
              <template v-if="formError.sheetTitle">
                <div class="field-error-message" v-for="error in formError.sheetTitle" :key="error">
                  {{ error }}
                </div>
              </template>
            </div>
            <div class="pl-3">
              <label>Cell reference (e.g. A7)</label>
              <input
                type="text"
                class="input-field grey-bg thin"
                :class="{ 'errored-field': formError.cell }"
                :value="formData.cell"
                @keyup="onCellAddressChange($event.target.value)"
              />
              <template v-if="formError.cell">
                <div class="field-error-message" v-for="error in formError.cell" :key="error">
                  {{ error }}
                </div>
              </template>
            </div>
          </div>
          <div class="d-flex">
            <div class="checkin-value">
              <label>KR Check-in value (#)</label>
              <input
                type="text"
                class="text-normal bold text-center input-field grey-bg fat"
                disabled
                :value="cellValueFetching ? 'Fetching...' : formData.cellValue"
              />
              <div class="d-flex align-items-center justify-content-center mt-2">
                <span>{{ kr.start }}</span>
                <span class="pr-1 pl-1 arrow-next">➝</span>
                <span>{{ kr.end }}</span>
              </div>
            </div>
            <div class="flex-grow-1 info pl-5 d-flex align-items-center">
              We’ll fetch this KR check-in value. Edit KR to set start and end range
            </div>
          </div>
        </form>
      </div>
      <div class="card-gutter mx-3" v-if="formError.errors">
        <error-alert
          class="m-0"
          :showError="showFormError"
          @show="showFormError = $event"
          :errors="formError.errors"
        />
      </div>
      <div class="d-flex justify-content-between footer mx-3">
        <div class="d-flex">
          <b-button class="mr-2 px-4" size="sm" pill variant="primary" @click="connect"
            >Connect
          </b-button>
          <b-button
            class="border-0"
            size="sm"
            pill
            variant="outline-secondary"
            @click="$refs.modal.hide()"
            >Cancel
          </b-button>
        </div>
        <b-button
          @click="showRemoveConfirmation = true"
          v-if="kr.extLookup === EXT_LOOKUP.GOOGLE_SPREADSHEET"
          variant="link"
          class="text-error add-new-btn p-0 pt-1 align-self-end"
          >Remove google connect
        </b-button>
      </div>
      <confirm-dialog
        v-model="showRemoveConfirmation"
        @ok="removeConnection"
        ok-title="Yes, Remove"
        :hide-header="true"
      >
        Please confirm that you want to remove google connect
      </confirm-dialog>
    </div>
  </b-modal>
</template>

<script>
import { EXT_LOOKUP } from '@/constants/kr'
import { BButton, BModal } from 'bootstrap-vue'
import * as R from 'ramda'
import TextArea from '@/components/form/TextArea'
import ErrorAlert from '@/components/ErrorAlert'
import AvatarTitle from '@/components/skeletons/AvatarTitle'
import ConfirmDialog from '@/components/ConfirmDialog'
import debounce from '@/utils/debounce'
import service from '@/service'
import { DEFAULT_ERROR } from '@/constants/messages'
import EventBus from '@/EventBus'
import GoogleSpreadsheetMixin from '@/mixins/GoogleSpreadsheetMixin'

const GOOGLE_SPREADSHEETS_SCOPE = 'https://www.googleapis.com/auth/spreadsheets.readonly'

/**
 * This component can be used to connect a KR to Google Spreadsheet
 * Props:
 *  - kr
 */
export default {
  name: 'KrGoogleConnect',
  props: ['kr', 'show', 'orgId', 'goalId'],
  mixins: [GoogleSpreadsheetMixin],
  components: {
    BModal,
    ConfirmDialog,
    TextArea,
    BButton,
    AvatarTitle,
    ErrorAlert
  },
  data() {
    return {
      externalAuthId: null,
      fetchingSheetInfo: false,
      cellValueFetching: false,
      showFormError: false,
      showRemoveConfirmation: false,
      sheetInfo: null,
      formData: {
        spreadsheetUrl: '',
        spreadsheetTitle: '',
        cell: null,
        sheetTitle: '',
        cellValue: null
      },
      formError: {
        cell: null,
        spreadsheetUrl: null,
        permission: null
      },
      authorizeGoogleListener: null,
      authorizeGooglePopup: null,
      EXT_LOOKUP
    }
  },
  computed: {
    spreadsheetName() {
      return R.path(['properties', 'title'], this.sheetInfo)
    },
    sheets() {
      return R.propOr([], 'sheets', this.sheetInfo).map(R.path(['properties', 'title']))
    },
    goal() {
      return this.$store.getters.goalById(this.goalId)
    }
  },
  methods: {
    async authorizeGoogle() {
      this.authorizeGooglePopup = window.open(
        '/authorize-google?scope=' + encodeURIComponent(GOOGLE_SPREADSHEETS_SCOPE),
        'authorize-google'
      )
    },
    async connect() {
      if (this.formData.cell && (this.formError.cell || this.cellValueFetching)) return // we need to ensure that user that typed-in valid cell address
      try {
        if (R.pathEq(['kr', 'extLookup'], EXT_LOOKUP.GOOGLE_SPREADSHEET, this)) {
          // edit
          await service.client.patch(
            `/exts/${this.orgId}/google_spreadsheets/links/${this.kr.id}/`,
            {
              spreadsheet_url: this.formData.spreadsheetUrl,
              spreadsheet_title: this.formData.spreadsheetTitle,
              sheet_title: this.formData.sheetTitle,
              cell: this.formData.cell,
              externalAuthId: this.externalAuthId
            }
          )
          EventBus.$emit('ADD_TOAST', { message: 'Changes saved successfully' })
          this.$refs.modal.hide()
        } else {
          // create
          await service.client.post(`/exts/${this.orgId}/google_spreadsheets/links/`, {
            key_result_id: this.kr.id,
            spreadsheet_url: this.formData.spreadsheetUrl,
            spreadsheet_title: this.formData.spreadsheetTitle,
            sheet_title: this.formData.sheetTitle,
            cell: this.formData.cell,
            externalAuthId: this.externalAuthId
          })
          EventBus.$emit('ADD_TOAST', { message: 'Changes saved successfully' })
          this.updateKR({ extLookup: EXT_LOOKUP.GOOGLE_SPREADSHEET })
          this.$refs.modal.hide()
        }
      } catch (e) {
        const details = R.path(['response', 'data', 'detail'], e)
        if (details) {
          this.formError = details
        } else {
          this.formError.errors = [R.pathOr(DEFAULT_ERROR, ['response', 'data', 'message'], e)]
          this.showFormError = true
        }
      }
    },
    fetchSheetDetails: debounce(async function() {
      const spreadsheetUrl = this.formData.spreadsheetUrl
      if (!spreadsheetUrl) {
        return
      }
      try {
        this.fetchingSheetInfo = true
        this.formError.cell = null
        this.formError.permission = null
        this.formError.spreadsheetUrl = null
        const response = await service.client.get(`/exts/${this.orgId}/google_spreadsheets/info/`, {
          params: {
            spreadsheet_url: encodeURI(spreadsheetUrl),
            external_auth_id: this.externalAuthId
          }
        })
        this.sheetInfo = response.data.sheetInfo
        this.formData.spreadsheetTitle = this.sheetInfo.properties.title
        this.fetchingSheetInfo = false
        this.formError.spreadsheetUrl = null
      } catch (e) {
        this.fetchingSheetInfo = false
        this.formError.spreadsheetUrl = [e.response?.data?.message]
        this.formError.permission = e.response?.status === 403
        this.sheetInfo = null
      }
    }, 500),
    onURLInput: function(value) {
      this.formData.spreadsheetUrl = value
      if (!this.formData.spreadsheetUrl) {
        this.sheetInfo = null
      } else {
        this.fetchSheetDetails()
      }
    },
    fetchCellValue: debounce(async function() {
      const { spreadsheetUrl, sheetTitle, cell } = this.formData
      if (!(spreadsheetUrl && sheetTitle && cell)) {
        return
      }
      try {
        this.cellValueFetching = true
        this.formError.cell = null
        this.formData.cellValue = await this.lookupCellValue(
          this.orgId,
          spreadsheetUrl,
          sheetTitle,
          cell,
          this.externalAuthId
        )
        this.cellValueFetching = false
      } catch (e) {
        this.cellValueFetching = false
        this.formError.cell = [R.pathOr(DEFAULT_ERROR, ['response', 'data', 'message'], e)]
      }
    }, 500),
    onCellAddressChange: function(value) {
      this.formData.cell = value
      if (!this.formData.cell) {
        this.formData.cellValue = null
      } else if (this.formData.sheetTitle) {
        this.fetchCellValue()
      }
    },
    updateKR(data) {
      const krIndex = R.findIndex(R.propEq('id', this.kr.id), this.goal.keyResults)
      const updatedKR = { ...this.goal.keyResults[krIndex], ...data }
      const updatedGoal = R.assocPath(['keyResults', krIndex], updatedKR, this.goal)
      this.$store.commit('SET_GOAL', updatedGoal)
    },
    async removeConnection() {
      await service.client.delete(`/exts/${this.orgId}/google_spreadsheets/links/${this.kr.id}/`)
      this.updateKR({ extLookup: 'none' })
      this.$refs.modal.hide()
      EventBus.$emit('ADD_TOAST', {
        message: 'Changes saved successfully'
      })
    },
    onSheetChange() {
      if (this.formData.cell) {
        this.fetchCellValue()
      }
    }
  },
  watch: {
    async show(modalShown) {
      if (modalShown && R.pathEq(['kr', 'extLookup'], EXT_LOOKUP.GOOGLE_SPREADSHEET, this)) {
        const { spreadsheetTitle, sheetTitle, spreadsheetUrl, cell, id } = await this.getLinkInfo(
          this.orgId,
          this.kr.id
        )
        this.formData = { spreadsheetTitle, sheetTitle, spreadsheetUrl, cell, id }
        this.fetchSheetDetails()
        this.fetchCellValue()
      } else {
        this.formData = {}
        this.sheetInfo = null
        this.formError = {}
      }
    }
  },
  mounted() {
    this.authorizeGoogleListener = event => {
      if (event.origin !== window.location.origin) {
        return
      }
      if (event.source !== this.authorizeGooglePopup) {
        return
      }
      this.externalAuthId = event.data.externalAuthId
      this.authorizeGooglePopup?.close()
      this.authorizeGooglePopup = null
      this.fetchSheetDetails()
      this.fetchCellValue()
    }
    window.addEventListener('message', this.authorizeGoogleListener)
  },
  beforeDestroy() {
    window.removeEventListener('message', this.authorizeGoogleListener)
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import '~@/styles/styleConfigurations';
@import '~@/styles/forms';
@import '~@/styles/errors';

.public-share-icon {
  display: inline-block;
  height: 28px;
  width: 28px;
  background: url(~@/assets/public-share.svg) no-repeat;
}
.info {
  color: $secondary;
}
/deep/ textarea {
  &,
  &:focus {
    color: $brand;
  }
}
.google-sheet-icon {
  display: inline-block;
  height: 22px;
  width: 16px;
  background: url(~@/assets/google-sheet.svg) no-repeat;
}
.header {
  margin-bottom: 20px;
}
.aligner {
  padding-left: 12px;
}
.skeleton {
  margin-bottom: 60px;
}
.custom-select {
  border-radius: $rounded-pill;
  &.thin {
    padding-top: 2px;
    padding-bottom: 2px;
    height: auto;
  }
}
input {
  display: block;
  &.thin {
    padding-top: 3.5px;
    padding-bottom: 3.5px;
  }
  &.fat {
    padding: 12px 0;
    border-radius: 50px;
  }
}
.arrow-next {
  color: #8a8a8a;
}
.checkin-value {
  width: 190px;
  input {
    cursor: not-allowed;
  }
}
.field-error-message {
  word-break: break-word;
}
.text-error:hover {
  color: $error;
}
.choose-sheet-field {
  flex-basis: 220px;
  flex-shrink: 0;
}
.footer {
  position: sticky;
  bottom: -1px;
  padding: 16px;
  background-color: white;
}
/deep/ .modal-body {
  padding-bottom: 0;
  padding-top: 0;
}
.header {
  position: sticky;
  top: -1px;
  background-color: white;
  z-index: 1;
  padding: 16px;
  padding-top: 32px;
}
</style>
